import React, { useState } from 'react';
import { ShieldCheck, Lock, Mail, ChevronDown, ChevronUp, Link2, Copy } from 'lucide-react';
import { toast } from 'react-hot-toast';

const LoginPage = ({ onLogin, error }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (typeof onLogin === 'function') {
      onLogin(username, password);
    }
  };

  const handleRequestInvite = async (e) => {
    e.preventDefault();
    const email = inviteEmail.trim();
    if (!email) return;
    setInviteSending(true);
    setInviteLink(null);
    try {
      const { api } = await import('../services/api.js');
      const res = await api.invites.create(email);
      setInviteLink(res?.inviteLink || null);
      if (res?.emailSent) {
        toast.success(`Invite sent to ${email}. Check your inbox.`);
      } else {
        toast.success('Invite created. Use the link below to set your password (email was not sent).');
      }
      setInviteEmail('');
    } catch (err) {
      const msg = err?.payload?.error || err?.message || 'Could not send invite.';
      toast.error(msg);
      if (err?.status === 401) {
        toast.error('Request was rejected. Ensure the app has the correct API key configured.');
      }
    } finally {
      setInviteSending(false);
    }
  };

  const copyInviteLink = () => {
    if (!inviteLink) return;
    navigator.clipboard.writeText(inviteLink).then(() => toast.success('Link copied to clipboard.'));
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-indigo-100 bg-white p-10 shadow-xl">
        <div className="mb-8 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white shadow-lg">
            <ShieldCheck size={28} />
          </div>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">Sign in to MarketReach</h2>
          <p className="mt-2 text-sm text-gray-500">Enter the support credentials to continue.</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-semibold text-gray-700">
              Email
            </label>
            <input
              id="username"
              type="email"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              className="mt-2 w-full rounded-lg border border-gray-200 px-4 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              placeholder="support@tradeitsolutions.com"
              autoComplete="email"
              required
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-semibold text-gray-700">
              Password
            </label>
            <div className="mt-2 relative">
              <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-lg border border-gray-200 px-10 py-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                placeholder="Enter password"
                autoComplete="current-password"
                required
              />
            </div>
          </div>
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
          <button
            type="submit"
            className="w-full rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
          >
            Sign in
          </button>

          <div className="pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setShowInvite(!showInvite)}
              className="w-full flex items-center justify-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
            >
              {showInvite ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              Don't have an account? Request an invite
            </button>
            {showInvite && (
              <div className="mt-4 space-y-3">
                <form onSubmit={handleRequestInvite} className="space-y-3">
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="Your email"
                      className="w-full rounded-lg border border-gray-200 pl-10 pr-4 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      disabled={inviteSending}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={inviteSending}
                    className="w-full rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-60"
                  >
                    {inviteSending ? 'Sending…' : 'Send me an invite link'}
                  </button>
                </form>
                {inviteLink && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-2">
                    <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                      <Link2 size={16} /> Use this link to set your password
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={inviteLink}
                        className="flex-1 rounded border border-amber-200 bg-white px-2 py-1.5 text-xs text-gray-700"
                      />
                      <button
                        type="button"
                        onClick={copyInviteLink}
                        className="rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 flex items-center gap-1"
                      >
                        <Copy size={14} /> Copy
                      </button>
                    </div>
                    <a
                      href={inviteLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                    >
                      Open link →
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginPage;
